/**
 * Resolving a Voluum click id (volcid) from the DTP loader.
 *
 * Paid traffic lands with a `cpid` but no `volcid` — the click id only exists once the
 * DTP loader in <head> has round-tripped to Voluum and attached `getClickID` to the
 * global `dtpCallback`. This resolves that handshake, or gives up.
 *
 * Deliberately Vue-free and side-effect-free so it can be kicked off from app.js on the
 * same tick as window.volcidData, rather than waiting on a lazily-imported component
 * chunk to download first.
 *
 * SerpContainer.vue still carries its own copy of this flow; it also writes cookies and
 * fires sendRegSig, neither of which applies off the SERP. Folding it into this util is
 * a separate change — see the note in initDtpFlow there.
 */

// The resolver's cap must stay strictly below DTP_WAIT_MS so that volcidData.ready is
// always flipped by the resolver itself rather than by a waiter timing out. If a waiter
// wins the race it resolves with ready still false, and the next waiter then burns
// another full timeout for nothing.
export const DTP_RESOLVE_MS = 2000;
export const DTP_WAIT_MS = 2500;

const POLL_INTERVAL_MS = 100;

/**
 * Always resolves — never rejects. An empty string means "no click id", whatever the
 * reason; callers normalise that to 'NA' at the point of use so the analytics stream
 * carries one failure token rather than three.
 *
 * @param {number} maxWaitMs
 * @returns {Promise<string>}
 */
export function resolveDtpClickId(maxWaitMs = DTP_RESOLVE_MS) {
    return new Promise((resolve) => {
        const startedAt = Date.now();
        let settled = false;

        const finish = (clickId, reason) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timeoutHandle);
            if (!clickId && reason && typeof Sentry !== 'undefined') {
                // One report per page load, so "the loader never arrived" stays
                // distinguishable from "it arrived without the getter" in production.
                //
                // Deliberately an Error rather than captureMessage: the beforeSend filter
                // in layouts/scripts/dochead reads the message off the exception and ends
                // with `if (!msg) return null`, so a plain captureMessage is dropped
                // before it leaves the browser. Note Sentry runs at sampleRate 0.25, so
                // treat the volume as a signal of a systemic failure, not an exact count.
                Sentry.captureException(new Error('dtp volcid unresolved: ' + reason));
            }
            resolve(clickId || '');
        };

        const timeoutHandle = setTimeout(() => {
            const hasDtp = typeof window.dtpCallback === 'function';
            const hasGetter = hasDtp && typeof window.dtpCallback.getClickID === 'function';
            finish('', 'timeout after ' + maxWaitMs + 'ms; hasDtp=' + hasDtp + '; hasGetClickID=' + hasGetter);
        }, maxWaitMs);

        const register = () => {
            if (settled) {
                return;
            }

            // The loader defines dtpCallback synchronously in <head>, but it is emitted
            // by a Blade partial we do not control the timing of, so poll rather than
            // assume it is already there.
            if (typeof window.dtpCallback !== 'function') {
                if (Date.now() - startedAt >= maxWaitMs) {
                    finish('', 'dtpCallback global never became available');
                    return;
                }
                setTimeout(register, POLL_INTERVAL_MS);
                return;
            }

            window.dtpCallback((payload) => {
                if (settled) {
                    return;
                }
                try {
                    // Two ways in, because the remote script only attaches getClickID
                    // when Voluum actually issued a click id - the response literally
                    // reads `c.clickId && (dtpCallback.getClickID = ...)`. It always
                    // invokes the callback though, passing either the id or the whole
                    // config depending on page type, so read the argument as well.
                    let clickId = '';
                    if (typeof window.dtpCallback.getClickID === 'function') {
                        clickId = window.dtpCallback.getClickID();
                    }
                    if (!clickId && payload) {
                        clickId = typeof payload === 'string' ? payload : (payload.clickId || '');
                    }
                    finish(clickId, clickId ? null : 'no clickId from dtpCallback');
                } catch (error) {
                    finish('', 'execution error: ' + ((error && error.message) || error));
                }
            });
        };

        register();
    });
}
